import React, { useState, useEffect } from "react";
import ReactPaginate from "react-paginate";
import { IoCall } from "react-icons/io5";
import { FaCalendarAlt } from "react-icons/fa";
import { MdOutlineVisibility, MdOutlineVisibilityOff } from "react-icons/md";
import { myLeads, getAllLeads } from "../Api/api";
import { toast } from "react-toastify";
import AdminViewToggle from "../Common/AdminViewToggle";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from 'moment';
import { useAuth } from "../Auth/AuthContext";
import { useRegions } from '../../context/RegionContext';
import DownloadTableData from '../Common/DownloadTableData';

const PendingTraining = () => {
  const { user, logout, selectedRegion } = useAuth();
  const { getRegionName } = useRegions();
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [filter, setFilter] = useState("all");
  const [currentFilterDates, setCurrentFilterDates] = useState({ from: null, to: null });
  const [currentFilterStatus, setCurrentFilterStatus] = useState("IN_TRAINING");
  const [showAllView, setShowAllView] = useState(false);
  const [visiblePhoneId, setVisiblePhoneId] = useState(null);
  const [trainingFilter, setTrainingFilter] = useState({
    D1: false,
    D2: false,
    D3: false,
    D4: false,
  });
  const [allTrainingData, setAllTrainingData] = useState([]);
  const [filteredTrainingData, setFilteredTrainingData] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const getPendingTraining = async (
    limit,
    offset,
    from_date = null,
    to_date = null,
    status = null
  ) => {
    try {
      setIsLoading(true);
      const selectedRegion = localStorage.getItem("selectedRegion") || "1";
      const region_ids = selectedRegion;
      const fetchFunction = showAllView ? getAllLeads : myLeads;
      const response = await fetchFunction(
        limit,
        offset,
        from_date,
        to_date,
        null,
        status,
        region_ids
      );
      setAllTrainingData(response.items || []);
      setTotalItems(response.total || 0);
    } catch (error) {
      console.error("Failed to get Pending Training Data:", error);
      toast.error("Failed to get Pending Training Data");
    } finally {
      setIsLoading(false);
    }
  };

  const loadInitialData = async () => {
    const from = fromDate?.toISOString() || null;
    const to = toDate?.toISOString() || null;
    setCurrentFilterDates({ from, to });
    setCurrentFilterStatus("IN_TRAINING");
    await getPendingTraining(
      itemsPerPage,
      0,
      from,
      to,
      "IN_TRAINING"
    );
  };

  const handlePageClick = (event) => {
    const newPage = event.selected;
    setCurrentPage(newPage);
    getPendingTraining(
      itemsPerPage,
      newPage * itemsPerPage,
      currentFilterDates.from,
      currentFilterDates.to,
      currentFilterStatus
    );
  };

  const handleDateFilter = async () => {
    setCurrentPage(0);
    await loadInitialData();
  };

  useEffect(() => {
    loadInitialData();
  }, [showAllView, fromDate, toDate]);

  useEffect(() => {
    // Filter training data based on selected region
    if (allTrainingData && allTrainingData.length > 0) {
      const selectedRegionName = getRegionName(selectedRegion);
      const filtered = allTrainingData.filter(item => {
        const geo = item.details_dict?.CLUSTER?.toLowerCase() || '';
        return geo.includes(selectedRegionName);
      });
      setFilteredTrainingData(filtered);
    } else {
      setFilteredTrainingData([]);
    }
  }, [allTrainingData, selectedRegion, getRegionName]);

  const formatDate = (incomingDate) => {
    const date = new Date(incomingDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // months are zero-indexed
    const year = date.getFullYear();

    const formattedDate = `${day}-${month}-${year}`;
    return formattedDate;
  };


  const isMobile = () => {
    const userAgent = navigator.userAgent || navigator.platform;
    return (
      /Mobi|Android/i.test(userAgent) || /iPhone|iPad|iPod/i.test(userAgent)
    );
  };

  const clickToCall = async (phoneNumber) => {
    if (isMobile()) {
      window.location.href = `tel:+91${phoneNumber}`;
    } else {
      navigator.clipboard
        .writeText(phoneNumber)
        .then(() => {
          toast.success("Phone number copied to clipboard");
        })
        .catch((err) => {
          console.error(err);
          toast.error("Error copying phone number");
        });
    }
  };

  const getNextTrainingDay = (runner)=>{
    const { training_data } = runner || {};
    if (!training_data) {
      return "Not Started";
    }
    const { day1, day2, day3, day4 } = training_data;
    const calculateNextDate = (dateString) => {
      const date = new Date(dateString);
      date.setDate(date.getDate() + 1); // Add one day
      return date.toISOString().split("T")[0]; // Return the date in 'YYYY-MM-DD' format
    };
    if (day1 && !day2) {
      return formatDate(calculateNextDate(day1));
    }
    if (day2 && !day3) {
      return formatDate(calculateNextDate(day2));
    }
    if (day3 && !day4) {
      return formatDate(calculateNextDate(day3));
    }
    if (day1 && day2 && day3 && day4) {
      return "Training Completed";
    }
    return "Invalid training data";
  }
  
  const filterData = (data) => {
    return data.filter((item) => {
      const { training_data } = item?.runner || {};
      
      if (!training_data) {
        return (
          trainingFilter.D1 === false ||
          trainingFilter.D2 === false ||
          trainingFilter.D3 === false ||
          trainingFilter.D4 === false
        );
      }

      const { day1, day2, day3, day4 } = training_data;

      // Filter conditions based on checked filters
      const filterD1 = trainingFilter.D1 ? day1 == null || day1 === "" : true;
      const filterD2 = trainingFilter.D2 ? day2 == null || day2 === "" : true;
      const filterD3 = trainingFilter.D3 ? day3 == null || day3 === "" : true;
      const filterD4 = trainingFilter.D4 ? day4 == null || day4 === "" : true;

      // Combine training filters
      return filterD1 && filterD2 && filterD3 && filterD4;
    });
  };

  // Apply client-side D1-D4 training day filters to the current page's data
  const filteredData = filterData(filteredTrainingData);
  const pageCount = Math.ceil(totalItems / itemsPerPage);
  const currentItems = filteredData;

  const tableHeaders = [
    "Agent",
    "Full Name",
    "Phone",
    "Call",
    "Date of Registration",
    "Next Training Date",
    "Geo",
    "D1",
    "D2",
    "D3",
    "D4"
  ];

  const formatTableData = (data) => {
    return data.map(item => [
      item?.agent_name || "N/A",
      item?.name || "",
      item?.phone || "",
      "", // Call column is empty in CSV
      item?.runner?.created_at ? formatDate(item.runner.created_at) : "Not Registered",
      getNextTrainingDay(item.runner),
      item?.details_dict?.CLUSTER || "Did Not Answered",
      item?.runner?.training_data?.day1 ? "Yes" : "No",
      item?.runner?.training_data?.day2 ? "Yes" : "No",
      item?.runner?.training_data?.day3 ? "Yes" : "No",
      item?.runner?.training_data?.day4 ? "Yes" : "No"
    ]);
  };

  // Add this new function to handle phone visibility toggle
  const handlePhoneVisibility = (id) => {
    setVisiblePhoneId(visiblePhoneId === id ? null : id);
  };

  return (
    <div className="container mx-auto my-8 p-4">
      <div className="flex flex-col gap-6">
        {/* Date Filter Section */}
        <div className="bg-white p-2 rounded-lg shadow">
          <h3 className="text-sm font-semibold mb-2">Date Filter</h3>
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex items-center gap-1">
              <FaCalendarAlt className="text-gray-500 text-sm" />
              <DatePicker
                selected={fromDate}
                onChange={date => setFromDate(date)}
                selectsStart
                startDate={fromDate}
                endDate={toDate}
                maxDate={toDate || new Date()}
                placeholderText="From Date"
                dateFormat="dd/MM/yyyy"
                className="px-2 py-1 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
            <div className="flex items-center gap-1">
              <FaCalendarAlt className="text-gray-500 text-sm" />
              <DatePicker
                selected={toDate}
                onChange={date => setToDate(date)}
                selectsEnd
                startDate={fromDate}
                endDate={toDate}
                minDate={fromDate}
                maxDate={new Date()}
                placeholderText="To Date"
                dateFormat="dd/MM/yyyy"
                className="px-2 py-1 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
            </div>
            <button
              onClick={handleDateFilter}
              className="px-3 py-1 text-sm bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition-colors"
            >
              Apply Filter
            </button>
            <button
              onClick={() => {
                setFromDate(null);
                setToDate(null);
                handleDateFilter();
              }}
              className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Clear Filter
            </button>
          </div>
        </div>

        {/* Filter Section */}
        <div className="flex flex-wrap gap-2 sm:gap-4 justify-between items-center">
          <div className="flex flex-wrap gap-2 sm:gap-4">
            <button
              onClick={() => {
                setCurrentPage(0);
                setCurrentFilterDates({ from: null, to: null });
                setCurrentFilterStatus("IN_TRAINING");
                getPendingTraining(itemsPerPage, 0, null, null, "IN_TRAINING");
                setFilter("all");
              }}
              className={`p-2 text-sm sm:text-base ${
                filter === "all"
                  ? "bg-pink-500 text-white"
                  : "bg-white text-gray-700"
              } hover:bg-pink-600 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-400 shadow-sm hover:shadow-md active:shadow-inner rounded`}
            >
              All
            </button>
            <button
              onClick={() => {
                const today = moment();
                const to_date = today.format();
                const from_date = today.subtract(10, 'days').format();
                setCurrentPage(0);
                setCurrentFilterDates({ from: from_date, to: to_date });
                setCurrentFilterStatus("IN_TRAINING");
                getPendingTraining(
                  itemsPerPage,
                  0,
                  from_date,
                  to_date,
                  "IN_TRAINING"
                );
                setFilter("hot");
              }}
              className={`p-2 text-sm sm:text-base ${
                filter === "hot"
                  ? "bg-pink-500 text-white"
                  : "bg-white text-gray-700"
              } hover:bg-pink-600 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-400 shadow-sm hover:shadow-md active:shadow-inner rounded`}
            >
              Hot (L10)
            </button>
            <button
              onClick={() => {
                const today = moment();
                const to_date = today.subtract(11, 'days').format();
                const from_date = today.subtract(9, 'days').format();
                setCurrentPage(0);
                setCurrentFilterDates({ from: from_date, to: to_date });
                setCurrentFilterStatus("IN_TRAINING");
                getPendingTraining(
                  itemsPerPage,
                  0,
                  from_date,
                  to_date,
                  "IN_TRAINING"
                );
                setFilter("warm");
              }}
              className={`p-2 text-sm sm:text-base ${
                filter === "warm"
                  ? "bg-pink-500 text-white"
                  : "bg-white text-gray-700"
              } hover:bg-pink-600 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-400 shadow-sm hover:shadow-md active:shadow-inner rounded`}
            >
              Warm (L11-20)
            </button>
            <button
              onClick={() => {
                const today = moment();
                const to_date = today.subtract(21, 'days').format();
                setCurrentPage(0);
                setCurrentFilterDates({ from: null, to: to_date });
                setCurrentFilterStatus("IN_TRAINING");
                getPendingTraining(
                  itemsPerPage,
                  0,
                  null,
                  to_date,
                  "IN_TRAINING"
                );
                setFilter("cold");
              }}
              className={`p-2 text-sm sm:text-base ${
                filter === "cold"
                  ? "bg-pink-500 text-white"
                  : "bg-white text-gray-700"
              } hover:bg-pink-600 transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-400 shadow-sm hover:shadow-md active:shadow-inner rounded`}
            >
              Cold (L20+)
            </button>
          </div>

          {/* Admin View Toggle */}
          <AdminViewToggle 
            showAllView={showAllView} 
            onToggle={() => setShowAllView(!showAllView)} 
          />
        </div>

        {/* Filter by Training Status */}
        <div className="mt-4">
          <label className="mr-2">Training Pending:</label>
          <input
            type="checkbox"
            name="D1"
            checked={trainingFilter.D1}
            onChange={(e) => setTrainingFilter({
              ...trainingFilter,
              [e.target.name]: e.target.checked,
            })}
            className="mr-2"
          />
          <label className="mr-4">D1 Pending</label>

          <input
            type="checkbox"
            name="D2"
            checked={trainingFilter.D2}
            onChange={(e) => setTrainingFilter({
              ...trainingFilter,
              [e.target.name]: e.target.checked,
            })}
            className="mr-2"
          />
          <label className="mr-4">D2 Pending</label>

          <input
            type="checkbox"
            name="D3"
            checked={trainingFilter.D3}
            onChange={(e) => setTrainingFilter({
              ...trainingFilter,
              [e.target.name]: e.target.checked,
            })}
            className="mr-2"
          />
          <label className="mr-4">D3 Pending</label>

          <input
            type="checkbox"
            name="D4"
            checked={trainingFilter.D4}
            onChange={(e) => setTrainingFilter({
              ...trainingFilter,
              [e.target.name]: e.target.checked,
            })}
            className="mr-2"
          />
          <label>D4 Pending</label>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
          </div>
        )}

        {/* Table with Registrations */}
        {!isLoading && (
          <div className="overflow-x-auto shadow-md sm:rounded-lg">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">Pending Training</h2>
              <DownloadTableData 
                data={formatTableData(filteredData)}
                headers={tableHeaders}
                filenamePrefix="pending_training"
                showAllView={showAllView}
              />
            </div>
            <table className="min-w-full text-sm text-left">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Agent
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Full Name
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Phone Number
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Call
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Date of Registration
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">
                    Next Training Date
                  </th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">Geo</th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">D1</th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">D2</th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">D3</th>
                  <th className="px-6 py-3 text-xs font-medium uppercase">D4</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr key={item.id} className="bg-white border-b">
                      <td className="px-6 py-4">{item?.agent_name}</td>
                      <td className="px-6 py-4">{item?.name}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handlePhoneVisibility(item.id)}
                            className={`p-2 rounded-lg transition-all duration-200 hover:bg-emerald-50 ${
                              visiblePhoneId === item.id ? 'text-emerald-600 bg-emerald-50' : 'text-emerald-500'
                            }`}
                            title={visiblePhoneId === item.id ? "Hide Phone Number" : "Show Phone Number"}
                          >
                            {visiblePhoneId === item.id ? (
                              <div className="flex items-center gap-3">
                                <span 
                                  className="no-select text-gray-700 font-medium" 
                                  style={{ 
                                    userSelect: 'none',
                                    WebkitUserSelect: 'none',
                                    msUserSelect: 'none',
                                    MozUserSelect: 'none'
                                  }}
                                >
                                  {item.phone}
                                </span>
                                <MdOutlineVisibilityOff className="w-5 h-5 hover:text-emerald-700" />
                              </div>
                            ) : (
                              <div className="flex items-center justify-center w-full">
                                <MdOutlineVisibility className="w-5 h-5 hover:text-emerald-700" />
                              </div>
                            )}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          className="text-pink-500"
                          onClick={() => clickToCall(item?.phone)}
                        >
                          <IoCall />
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        {item?.runner?.created_at === "" ||
                        item?.runner?.created_at == null
                          ? "Not Registered"
                          : formatDate(item?.runner?.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        {getNextTrainingDay(item.runner)}
                      </td>
                      <td className="px-6 py-4">
                        {item?.details_dict?.CLUSTER || "Did Not Answered"}
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={
                            item?.runner?.training_data?.day1 === "" ||
                            item?.runner?.training_data?.day1 == null
                              ? false
                              : true
                          }
                          disabled
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={
                            item?.runner?.training_data?.day2 === "" ||
                            item?.runner?.training_data?.day2 == null
                              ? false
                              : true
                          }
                          disabled
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={
                            item?.runner?.training_data?.day3 === "" ||
                            item?.runner?.training_data?.day3 == null
                              ? false
                              : true
                          }
                          disabled
                        />
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={
                            item?.runner?.training_data?.day4 === "" ||
                            item?.runner?.training_data?.day4 == null
                              ? false
                              : true
                          }
                          disabled
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="11" className="px-6 py-4 text-center">
                      No data found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!isLoading && pageCount > 1 && (
          <div className="mt-6 flex justify-center">
            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={handlePageClick}
              pageRangeDisplayed={5}
              pageCount={pageCount}
              previousLabel="< Previous"
              renderOnZeroPageCount={null}
              containerClassName="flex items-center space-x-2"
              pageClassName="px-3 py-1 rounded hover:bg-pink-100"
              pageLinkClassName="text-gray-700"
              activeClassName="bg-pink-500 text-white"
              previousClassName="px-3 py-1 rounded hover:bg-pink-100"
              nextClassName="px-3 py-1 rounded hover:bg-pink-100"
              disabledClassName="opacity-50 cursor-not-allowed"
              forcePage={currentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingTraining;
